import { CustomBenefitDTO } from 'probonio-shared-ui/api';
import type { CUSTOM_BENEFIT_ICONS } from 'probonio-shared-ui/component/icon';
import { CountryCode } from 'probonio-shared-ui/module/country/const';
import { useTenant } from 'probonio-shared-ui/module/me';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CAPITAL_FORMING_BENEFITS_HELP,
  EMPLOYEE_ACTIONS_HELP,
  EMPLOYEE_OFFERS_HELP,
  HEALTH_BUDGET_HELP,
} from './customBenefitTemplatesHTML';

export enum CustomBenefitTemplateKeys {
  CUSTOM_BENEFIT = 'CUSTOM_BENEFIT',
  HEALTH_BUDGET = 'HEALTH_BUDGET',
  EMPLOYEE_OFFERS = 'EMPLOYEE_OFFERS',
  EMPLOYEE_ACTIONS = 'EMPLOYEE_ACTIONS',
  CAPITAL_FORMING_BENEFITS = 'CAPITAL_FORMING_BENEFITS',
}

export interface CurrentCustomBenefitTemplate {
  customBenefit?: CustomBenefitDTO;
  customBenefitId?: string;
  key?: CustomBenefitTemplateKeys;
  benefit: CustomBenefitTemplate;
}

export interface CustomBenefitTemplate {
  iconValue: keyof typeof CUSTOM_BENEFIT_ICONS;
  title: string;
  description: string;
  imageUrl: string;
  defaultMonthlyGrant?: number;
  html?: string;
  showAsTile?: boolean;
  showVisualization?: boolean;
  enabled?: boolean;
  relevantForNonCash?: boolean;
  templateKey?: string;
  hasInfoText?: boolean;
  countryCode?: CountryCode;
}

export function useCustomBenefitTemplates() {
  const { tenant } = useTenant();
  const { t } = useTranslation('customBenefitsModule');

  const CustomBenefitTemplates: Record<CustomBenefitTemplateKeys, CustomBenefitTemplate> = useMemo(() => {
    return {
      [CustomBenefitTemplateKeys.HEALTH_BUDGET]: {
        iconValue: 'MedicalServices',
        imageUrl: '/image/customBenefits/banner/Gesundheitsbudget.jpg',
        title: t(`benefits.${CustomBenefitTemplateKeys.HEALTH_BUDGET}.title`),
        description: t(`benefits.${CustomBenefitTemplateKeys.HEALTH_BUDGET}.description`),
        hasInfoText: true,
        html: HEALTH_BUDGET_HELP,
        relevantForNonCash: true,
        templateKey: CustomBenefitTemplateKeys.HEALTH_BUDGET,
        countryCode: CountryCode.De,
      },
      [CustomBenefitTemplateKeys.EMPLOYEE_OFFERS]: {
        iconValue: 'CardGiftcard',
        imageUrl: '/image/customBenefits/banner/CorporateBenefits.jpg',
        title: t(`benefits.${CustomBenefitTemplateKeys.EMPLOYEE_OFFERS}.title`),
        description: t(`benefits.${CustomBenefitTemplateKeys.EMPLOYEE_OFFERS}.description`),
        html: EMPLOYEE_OFFERS_HELP,
        defaultMonthlyGrant: 0,
        showVisualization: false,
        templateKey: CustomBenefitTemplateKeys.EMPLOYEE_OFFERS,
        countryCode: CountryCode.De,
      },
      [CustomBenefitTemplateKeys.EMPLOYEE_ACTIONS]: {
        iconValue: 'Celebration',
        imageUrl: '/image/customBenefits/banner/Mitarbeiteraktionen.jpg',
        title: t(`benefits.${CustomBenefitTemplateKeys.EMPLOYEE_ACTIONS}.title`),
        description: t(`benefits.${CustomBenefitTemplateKeys.EMPLOYEE_ACTIONS}.description`),
        html: EMPLOYEE_ACTIONS_HELP,
        defaultMonthlyGrant: 0,
        showVisualization: false,
        templateKey: CustomBenefitTemplateKeys.EMPLOYEE_ACTIONS,
        countryCode: CountryCode.De,
      },
      [CustomBenefitTemplateKeys.CAPITAL_FORMING_BENEFITS]: {
        iconValue: 'Euro',
        imageUrl: '/image/customBenefits/banner/VWL.jpg',
        title: t(`benefits.${CustomBenefitTemplateKeys.CAPITAL_FORMING_BENEFITS}.title`),
        description: t(`benefits.${CustomBenefitTemplateKeys.CAPITAL_FORMING_BENEFITS}.description`),
        html: CAPITAL_FORMING_BENEFITS_HELP,
        templateKey: CustomBenefitTemplateKeys.CAPITAL_FORMING_BENEFITS,
        countryCode: CountryCode.De,
      },
      [CustomBenefitTemplateKeys.CUSTOM_BENEFIT]: {
        imageUrl: '',
        iconValue: 'Star',
        title: t(`benefits.${CustomBenefitTemplateKeys.CUSTOM_BENEFIT}.title`),
        description: t(`benefits.${CustomBenefitTemplateKeys.CUSTOM_BENEFIT}.description`),
      },
    };
  }, [t]);

  return useMemo(() => {
    const filtered: Partial<Record<CustomBenefitTemplateKeys, CustomBenefitTemplate>> = {};

    for (const key in CustomBenefitTemplates) {
      const value = CustomBenefitTemplates[key as CustomBenefitTemplateKeys];
      if (!value.countryCode || (tenant && value.countryCode === tenant.countryCode)) {
        filtered[key as CustomBenefitTemplateKeys] = value;
      }
    }

    return filtered;
  }, [tenant, CustomBenefitTemplates]);
}
